import { useState, useRef } from 'react'
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/utils/cn'

interface ParsedRow {
  name: string
  url: string
  ownerEmail: string
}

interface ImportResult {
  row: ParsedRow
  success: boolean
  error?: string
  projectId?: string
}

interface BulkProjectImportProps {
  agencyId: string
  onComplete?: () => void
}

export function BulkProjectImport({ agencyId, onComplete }: BulkProjectImportProps) {
  const { user } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [results, setResults] = useState<ImportResult[]>([])
  const [progress, setProgress] = useState(0)

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setParseError(null)
    setResults([])

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string
        const lines = text.split('\n').map(l => l.trim()).filter(Boolean)

        if (lines.length < 2) {
          setParseError('CSV must have a header row and at least one data row')
          return
        }

        // Parse header
        const header = lines[0].toLowerCase().split(',').map(h => h.trim())
        const nameIdx = header.findIndex(h => h === 'name')
        const urlIdx = header.findIndex(h => h === 'url')
        const emailIdx = header.findIndex(h => h.includes('email') || h === 'owner_email')

        if (nameIdx === -1 || urlIdx === -1) {
          setParseError('CSV must have "name" and "url" columns. Optional: "owner_email"')
          return
        }

        const rows: ParsedRow[] = []
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',').map(c => c.trim())
          const name = cols[nameIdx] || ''
          const url = cols[urlIdx] || ''
          const ownerEmail = emailIdx >= 0 ? (cols[emailIdx] || '') : ''

          if (!name) continue

          rows.push({ name, url, ownerEmail })
        }

        if (rows.length === 0) {
          setParseError('No valid rows found in CSV')
          return
        }

        setParsedRows(rows)
      } catch {
        setParseError('Failed to parse CSV file')
      }
    }
    reader.readAsText(file)
  }

  const handleImport = async () => {
    if (!user || !agencyId || parsedRows.length === 0) return

    setImporting(true)
    setProgress(0)
    const importResults: ImportResult[] = []

    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i]
      try {
        // 1. Create project
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .insert({
            name: row.name,
            user_id: user.id,
            agency_id: agencyId,
            created_by: user.id,
            status: 'in_management',
            project_type: 'manage_only',
            source_url: row.url || null,
            current_step: 5,
            data: {
              phase5: {
                live_url: row.url || null,
              },
            },
          })
          .select('id')
          .single()

        if (projectError) throw projectError

        // 2. Create uptime monitor if URL provided
        if (row.url) {
          await supabase.from('uptime_monitors').insert({
            project_id: project.id,
            url: row.url,
            check_interval: 300, // 5 min
            is_up: true,
          })
        }

        // 3. If owner email, create client and set as stakeholder
        if (row.ownerEmail) {
          try {
            await supabase.functions.invoke('create-client', {
              body: {
                email: row.ownerEmail.toLowerCase().trim(),
                name: row.ownerEmail.split('@')[0],
                projectId: project.id,
                role: 'client',
              },
            })

            // Set access_scope to stakeholder
            await supabase
              .from('project_members')
              .update({ access_scope: 'stakeholder' })
              .eq('project_id', project.id)
              .eq('role', 'client')
          } catch (clientErr) {
            console.warn(`Client creation failed for ${row.ownerEmail}:`, clientErr)
            // Don't fail the whole row for client invite issues
          }
        }

        importResults.push({ row, success: true, projectId: project.id })
      } catch (err) {
        importResults.push({
          row,
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        })
      }

      setProgress(i + 1)
      setResults([...importResults])
    }

    setImporting(false)
    onComplete?.()
  }

  const successCount = results.filter(r => r.success).length
  const errorCount = results.filter(r => !r.success).length

  return (
    <div className="space-y-4">
      {/* File Upload */}
      {parsedRows.length === 0 && results.length === 0 && (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-framed-neutral-300 rounded-xl p-8 text-center cursor-pointer hover:border-framed-yellow-500 hover:bg-framed-yellow-50/30 transition-colors"
        >
          <Upload className="w-8 h-8 text-framed-neutral-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-framed-neutral-700">Upload CSV file</p>
          <p className="text-xs text-framed-neutral-500 mt-1">Format: name, url, owner_email</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      )}

      {parseError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {parseError}
        </div>
      )}

      {/* Preview Table */}
      {parsedRows.length > 0 && results.length === 0 && (
        <>
          <div className="bg-framed-neutral-100 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-framed-neutral-300 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-framed-neutral-500" />
              <span className="text-sm font-medium text-framed-neutral-800">
                {parsedRows.length} project{parsedRows.length !== 1 ? 's' : ''} to import
              </span>
            </div>
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-framed-neutral-300">
                    <th className="py-2 px-4 text-left font-medium text-framed-neutral-500">Name</th>
                    <th className="py-2 px-4 text-left font-medium text-framed-neutral-500">URL</th>
                    <th className="py-2 px-4 text-left font-medium text-framed-neutral-500">Owner Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-framed-neutral-200">
                  {parsedRows.map((row, i) => (
                    <tr key={i}>
                      <td className="py-2 px-4 text-framed-neutral-800">{row.name}</td>
                      <td className="py-2 px-4 text-framed-neutral-600 truncate max-w-[200px]">{row.url || '-'}</td>
                      <td className="py-2 px-4 text-framed-neutral-600">{row.ownerEmail || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleImport}
              disabled={importing}
              className="px-5 py-2.5 bg-framed-neutral-800 text-white rounded-xl font-medium hover:bg-framed-neutral-900 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing ({progress}/{parsedRows.length})
                </>
              ) : (
                'Start Import'
              )}
            </button>
            <button
              onClick={() => { setParsedRows([]); setParseError(null) }}
              disabled={importing}
              className="px-5 py-2.5 text-framed-neutral-600 bg-framed-neutral-100 rounded-xl font-medium hover:bg-framed-neutral-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Progress / Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-4 text-sm">
            {successCount > 0 && (
              <span className="text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                {successCount} imported
              </span>
            )}
            {errorCount > 0 && (
              <span className="text-red-700 flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                {errorCount} failed
              </span>
            )}
          </div>

          <div className="bg-framed-neutral-100 rounded-xl overflow-hidden max-h-[300px] overflow-y-auto">
            <div className="divide-y divide-framed-neutral-200">
              {results.map((result, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                  {result.success ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  )}
                  <span className="text-sm text-framed-neutral-800 flex-1">{result.row.name}</span>
                  {result.error && (
                    <span className="text-xs text-red-600 truncate max-w-[200px]">{result.error}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => { setParsedRows([]); setResults([]) }}
            className="px-5 py-2.5 text-framed-neutral-600 bg-framed-neutral-100 rounded-xl font-medium hover:bg-framed-neutral-200 transition-colors"
          >
            Import More
          </button>
        </div>
      )}
    </div>
  )
}
